import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "../../auth/access";
import { Box } from "@mui/material";
import type { PremiseConfig } from "@shared/types/foyer";
import { SCHEME_MANIFESTS } from "@shared/schemes/manifests";
import type { TemplateSubSection } from "../../components/PremiseSidebar";
import LivePreview from "../../components/template/LivePreview";
import TemplateSubEditor, {
  useTemplateApps,
} from "../../components/template/TemplateSubEditor";
import type { PremiseCommit } from "../../context/PremiseWorkspace";
import {
  PanelHeading,
  SectionList,
  StartNavLayout,
  startNavRoot,
} from "../../components/startnav/StartNavLayout";

/** What "Appearance" holds, in the order a place is set up: choose the
 *  template, then dress it. */
const APPEARANCE_ITEMS = [
  {
    key: "picker",
    label: "appearancePage.items.picker.label",
    subtitle: "appearancePage.items.picker.subtitle",
  },
  {
    key: "design",
    label: "appearancePage.items.design.label",
    subtitle: "appearancePage.items.design.subtitle",
  },
  {
    key: "typography",
    label: "appearancePage.items.typography.label",
    subtitle: "appearancePage.items.typography.subtitle",
  },
  {
    key: "background",
    label: "appearancePage.items.background.label",
    subtitle: "appearancePage.items.background.subtitle",
  },
  {
    key: "branding",
    label: "appearancePage.items.branding.label",
    subtitle: "appearancePage.items.branding.subtitle",
  },
  {
    key: "icons",
    label: "appearancePage.items.icons.label",
    subtitle: "appearancePage.items.icons.subtitle",
  },
] as const satisfies readonly {
  key: TemplateSubSection;
  label: string;
  subtitle: string;
}[];

/** Keys valid in `?item=` for this page - exported for the legacy redirect. */
export const APPEARANCE_KEYS = new Set<string>(
  APPEARANCE_ITEMS.map((i) => i.key)
);

/**
 * Start & navigation → Appearance ("Utseende"): the template choice and
 * everything about how it looks, as opposed to what it contains.
 */
export default function AppearancePage({
  premise,
  onCommit,
}: {
  premise: PremiseConfig;
  /** Saves each change and toasts it (PremiseWorkspace `commit`). */
  onCommit: PremiseCommit;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const [params, setParams] = useSearchParams();
  const apps = useTemplateApps(premise);
  const manifest = premise.scheme
    ? SCHEME_MANIFESTS[premise.scheme.key]
    : undefined;
  // Home cards are the care template's; nothing else draws them.
  const isCareTemplate = manifest?.screens.some((s) => s.id === "tv") ?? false;
  const items = APPEARANCE_ITEMS.filter(
    (i) => i.key !== "icons" || isCareTemplate
  )
    // Nothing to style until there is a template to style.
    .filter((i) => i.key === "picker" || !!premise.scheme);

  const requested = params.get("item") ?? "";
  const current = items.find((i) => i.key === requested) ?? items[0]!;
  const appearance = t("common:labels.appearance");

  return (
    <StartNavLayout
      crumbs={[startNavRoot(t), { label: appearance }]}
      pageTitle={appearance}
      pageSubtitle={t("appearancePage.pageSubtitle")}
      title={t(current.label)}
      subtitle={t(current.subtitle)}
      list={
        <SectionList
          groups={[
            {
              key: "appearance",
              title: appearance,
              subtitle: t("appearancePage.listSubtitle"),
              items: items.map((i) => ({ key: i.key, label: t(i.label) })),
            },
          ]}
          selected={current.key}
          onSelect={(key) =>
            setParams(
              (prev) => {
                const q = new URLSearchParams(prev);
                q.set("item", key);
                return q;
              },
              { replace: true }
            )
          }
        />
      }
      editor={
        <Box data-testid={`startnav-editor-${current.key}`}>
          <PermissionGate resource="tv-layout" level="administrate">
            <TemplateSubEditor
              premise={premise}
              sub={current.key}
              onCommit={onCommit}
              apps={apps}
              stateBar={false}
            />
          </PermissionGate>
        </Box>
      }
      preview={
        <Box>
          <PanelHeading
            title={t("startNavLayout.preview")}
            subtitle={t("appearancePage.previewSubtitle")}
          />
          <LivePreview premise={premise} maxWidth={440} hideLabel />
        </Box>
      }
    />
  );
}
